import { useEffect, useState } from 'react';
import axios from 'axios';
import Main from '../../../component/Main';
import NewCommonSelect from '../../../component/common/selectBox/NewCommonSelect';
import Pagination from '../../../component/common/dataGrid/Pagination';
import ExcelDownload from '../../../component/common/button/ExcelDownload';
import useModalStore from '../../../zustand/store/useModalStore';
import useConfirm from '../../../hooks/useConfirm';
import { useMenuStore } from '../../../zustand/store/useMenuStore';

import { useDynamicStyle } from '../../../hooks/useDynamicStyle';
import * as Icons from '../../../images/icons/icons_component';
import Loading from '../../../component/common/loading/Loading';

/**
 * NEDIS 시스템관리 > 기본정보관리 > 예외전송승인 (EMIMEMMK)
 * 기관이 신청한 예외전송 건을 승인/반려하거나 신청취소 처리 (목록 조회는 예외전송 승인조회와 동일한 API를 사용)
 */
const BdExceptionApp = () => {

    const admin = localStorage.getItem('userAuth') === 'J001' || localStorage.getItem('userAuth') === 'J002';
    const permissions = useMenuStore(state => state.permissions);

    const showNotiModal = useModalStore(state => state.showNotiModal);
    const confirm = useConfirm();

    // Dynamic Style Hook
    const { fontColor, primaryBackColor, lightBackColor, borderColorGray, hoverLightFontColor } = useDynamicStyle();

    const formatDate = (date) => {
        const y = date.getFullYear();
        const m = `${date.getMonth() + 1}`.padStart(2, '0');
        const d = `${date.getDate()}`.padStart(2, '0');
        return `${y}-${m}-${d}`;
    };

    // 그 주의 월요일 ~ 목요일
    const getSearchDay = () => {
        const today = new Date();
        const dayOfWeek = today.getDay(); // 0(일) ~ 6(토)
        const startOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;

        const startDate = new Date();
        startDate.setDate(today.getDate() + startOffset);

        const endDate = new Date();
        endDate.setDate(today.getDate() + startOffset + 3);

        return { fromDate: formatDate(startDate), toDate: formatDate(endDate) };
    };

    const defaultDay = getSearchDay();

    const [searchParams, setSearchParams] = useState({
        searchGrdc: '',
        searchLoca: '',
        searchEmog: '',
        searchFromdate: defaultDay.fromDate,
        searchTodate: defaultDay.toDate,
        searchGubun: '',
        searchIdno: '',
        pageNum: 1,
        displayRowsize: 50,
    });

    // 처리정보
    const [procParams, setProcParams] = useState({
        procFromdate: defaultDay.fromDate,
        procTodate: defaultDay.toDate,
        procState: 'A',
        procCmmt: '',
        procAll: false,
    });

    const [dataList, setDataList] = useState([]);
    const [checkItems, setCheckItems] = useState([]);
    const [isSearched, setIsSearched] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [cnlAllow, setCnlAllow] = useState('N');

    const [pageInfo, setPageInfo] = useState({
        currentPage: 1,
        itemsPerPage: 50,
        totalItems: 0,
    });

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setSearchParams(prev => ({ ...prev, [name]: value }));
    };

    const handleProcInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setProcParams(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    useEffect(() => {
        const fetchCnlAllow = async () => {
            try {
                const { data } = await axios.get('/ptmiapi/system/nedis/sendweb/selectCnlAllow.do');
                setCnlAllow(data);
            } catch (error) {
                console.error('신청취소 권한 조회 중 오류 발생:', error);
            }
        };
        fetchCnlAllow();
    }, []);

    useEffect(() => {
        setSearchParams(prev => ({
            ...prev,
            pageNum: pageInfo.currentPage,
            displayRowsize: pageInfo.itemsPerPage,
        }));
        if (isSearched) fetchData(pageInfo.currentPage, pageInfo.itemsPerPage);
    }, [pageInfo.currentPage, pageInfo.itemsPerPage]);

    const fetchData = async (pageNum, displayRowsize) => {
        try {
            setIsLoading(true);

            const params = { ...searchParams, pageNum: pageNum, displayRowsize: displayRowsize };

            const { data } = await axios.get('/ptmiapi/sendweb/selectEMIMEMMKList.do', { params: params });
            setDataList(data);
            setCheckItems([]);

            const cnt = await axios.get('/ptmiapi/sendweb/selectEMIMEMMKListCnt.do', { params: params });
            setPageInfo(prev => ({ ...prev, totalItems: cnt.data }));
        } catch (error) {
            console.error('예외전송승인 데이터 로딩 중 오류 발생:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const search = () => {
        if (!searchParams.searchFromdate || !searchParams.searchTodate) {
            showNotiModal('신청기간을 선택해주세요.');
            return;
        }
        if (searchParams.searchTodate < searchParams.searchFromdate) {
            showNotiModal('신청기간 종료일자가 시작일자보다 과거일 수 없습니다.');
            return;
        }
        setIsSearched(true);
        setPageInfo(prev => ({ ...prev, currentPage: 1 }));
        fetchData(1, pageInfo.itemsPerPage);
    };

    const handleAllCheck = (checked) => {
        setCheckItems(checked ? [...dataList] : []);
    };

    const handleSingleCheck = (checked, item) => {
        setCheckItems(prev => checked ? [...prev, item] : prev.filter(ci => ci.SEQ !== item.SEQ));
    };

    const handlePerPageChange = (e) => {
        setPageInfo(prev => ({ ...prev, itemsPerPage: parseInt(e.target.value), currentPage: 1 }));
    };

    const onHandlePage = (page) => {
        if (page !== pageInfo.currentPage) {
            setPageInfo(prev => ({ ...prev, currentPage: page }));
        }
    };

    // 승인/반려 처리
    const process = async () => {
        const sMode = procParams.procAll ? 'A' : 'U';

        if (!procParams.procFromdate || !procParams.procTodate) {
            showNotiModal('전송마감 날짜를 입력해주세요.');
            return;
        }
        if (procParams.procTodate < procParams.procFromdate) {
            showNotiModal('전송마감 종료일자가 시작일자보다 과거일 수 없습니다.');
            return;
        }
        if (!procParams.procCmmt) {
            showNotiModal('처리 사유를 입력해주세요.');
            return;
        }
        if (sMode === 'U' && checkItems.length === 0) {
            showNotiModal('승인/반려 처리할 항목을 선택해주세요.');
            return;
        }

        const stateName = procParams.procState === 'A' ? '승인' : '반려';

        const confirmMessage = sMode === 'A'
            ? `${searchParams.searchEmog ? '선택한 기관의' : '전체 기관의'} ${searchParams.searchFromdate} ~ ${searchParams.searchTodate} 신청 건 중 미승인 건을 일괄 ${stateName} 처리하시겠습니까?`
            : `선택한 ${checkItems.length}건을 ${stateName} 처리하시겠습니까?`;

        const ok = await confirm(confirmMessage);
        if (!ok) return;

        const targets = sMode === 'A'
            ? [{
                emmkEmcd: searchParams.searchEmog,
                emmkDateFrom: searchParams.searchFromdate,
                emmkDateTo: searchParams.searchTodate,
            }]
            : checkItems.map(item => ({
                emmkEmcd: item.EMMKEMCD,
                emmkIdno: item.EMMKIDNO,
                emmkIndt: item.EMMKINDT,
                emmkIntm: item.EMMKINTM,
                emmkTodt: item.EMMKTODT,
                emmkDtty: item.EMMKDTTY,
            }));

        try {
            setIsLoading(true);

            const { data } = await axios.post('/ptmiapi/system/nedis/sendweb/excepTransAppr.do', {
                targets: targets,
                emmkFtdt: procParams.procFromdate,
                emmkTodt: procParams.procTodate,
                emmkOkty: procParams.procState,
                emmkOkmm: procParams.procCmmt,
                sMode: sMode,
            });

            showNotiModal(data.resultMessage);
            if (!data.hasError) fetchData(pageInfo.currentPage, pageInfo.itemsPerPage);
        } catch (error) {
            console.error('예외전송 승인/반려 처리 중 오류 발생:', error);
            showNotiModal('처리에 실패하였습니다.');
        } finally {
            setIsLoading(false);
        }
    };

    // 신청취소 (상태값 D 변경)
    const cancelItem = async () => {
        if (checkItems.length === 0) {
            showNotiModal('신청취소할 항목을 선택해주세요.');
            return;
        }

        const ok = await confirm(`선택한 ${checkItems.length}건을 신청취소 하시겠습니까?`);
        if (!ok) return;

        const targets = checkItems.map(item => ({
            emmkEmcd: item.EMMKEMCD,
            emmkIdno: item.EMMKIDNO,
            emmkIndt: item.EMMKINDT,
            emmkIntm: item.EMMKINTM,
        }));

        try {
            setIsLoading(true);

            const { data } = await axios.post('/ptmiapi/system/nedis/sendweb/deleteEMIMEMMK.do', { targets: targets });

            showNotiModal(data.resultMessage);
            if (!data.hasError) fetchData(pageInfo.currentPage, pageInfo.itemsPerPage);
        } catch (error) {
            console.error('예외전송 신청취소 처리 중 오류 발생:', error);
            showNotiModal('신청취소에 실패하였습니다.');
        } finally {
            setIsLoading(false);
        }
    };

    const perPageOptions = Array.from({ length: 10 }, (_, i) => (i + 1) * 10);

    const dataHead = ['기관명', '지역', '기관종별', '등록번호', '내원일자', '시간', '데이터구분', '전송마감기간', '승인상태', '신청사유', '신청일자', '신청자', '승인사유', '승인일자', '승인자', '전송여부'];
    const dataColumn = ['EMOGDESC', 'EMOGLOCA', 'EMOGGRDC', 'EMMKMASK', 'EMMKINDTDESC', 'EMMKINTMDESC', 'EMMKDTTYDESC', 'EMMK_FT_TO_DT', 'EMMKOKTY', 'EMMKMEMO', 'EMMKDATE', 'EMMKUSER', 'EMMKOKMM', 'EMMKOKDT', 'EMMKOKUS', 'SENDYN'];
    const excelFileName = '예외전송승인';

    // 신청취소는 메뉴 삭제권한과 신청취소 허용계정을 모두 만족할 때만 노출
    const cancelBtnShow = (admin || permissions?.DLYN === 'Y') && cnlAllow === 'Y';

    return (
        <Main>
            <div className='com-search'>
                <table>
                    <colgroup>
                        <col width='9%' />
                        <col width='24%' />
                        <col width='9%' />
                        <col width='19%' />
                        <col width='9%' />
                        <col width='20%' />
                    </colgroup>
                    <tbody>
                        <tr>
                            <NewCommonSelect
                                hospitalTF={true}
                                searchParams={searchParams}
                                handleInputChange={handleInputChange}
                            />
                        </tr>
                        <tr>
                            <th>신청기간</th>
                            <td>
                                <input
                                    type='date'
                                    name='searchFromdate'
                                    value={searchParams.searchFromdate}
                                    onChange={handleInputChange}
                                />
                                &nbsp;~&nbsp;
                                <input
                                    type='date'
                                    name='searchTodate'
                                    value={searchParams.searchTodate}
                                    onChange={handleInputChange}
                                    min={searchParams.searchFromdate}
                                />
                            </td>
                            <th>구분</th>
                            <td>
                                <select
                                    name='searchGubun'
                                    className='w-full'
                                    value={searchParams.searchGubun}
                                    onChange={handleInputChange}>
                                    <option value=''>전체</option>
                                    <option value='A'>승인</option>
                                    <option value='N'>미승인</option>
                                    <option value='R'>반려</option>
                                    <option value='D'>신청취소</option>
                                </select>
                            </td>
                            <th>등록번호</th>
                            <td>
                                <input type='text' id='searchIdno' name='searchIdno' value={searchParams.searchIdno} onChange={handleInputChange} />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className='search_btn_wrapper'>
                <div className='flex items-center gap-2'>
                    <span className='w-40 bold'>[처리정보]</span>
                    <input type='date' name='procFromdate' value={procParams.procFromdate} onChange={handleProcInputChange} />
                    ~
                    <input type='date' name='procTodate' value={procParams.procTodate} onChange={handleProcInputChange} min={procParams.procFromdate} />
                    <select name='procState' value={procParams.procState} onChange={handleProcInputChange}>
                        <option value='A'>승인</option>
                        <option value='R'>반려</option>
                    </select>
                    <input type='text' name='procCmmt' className='h-7' placeholder='처리 사유' value={procParams.procCmmt} onChange={handleProcInputChange} />
                    <label htmlFor='procAll' className='w-44'><input type='checkbox' id='procAll' name='procAll' checked={procParams.procAll} onChange={handleProcInputChange} />일괄처리</label>
                    <button type='button' onClick={process} disabled={isLoading ? 'disabled' : ''} className={`search_btn ${primaryBackColor}`}>처리</button>
                    {cancelBtnShow &&
                        <button type='button' onClick={cancelItem} disabled={isLoading ? 'disabled' : ''} className='search_btn bg_tr_primary'>신청취소</button>
                    }
                </div>
                <div className='btn_container'>
                    <select name='itemsPerPage' className='page_select' value={pageInfo.itemsPerPage} onChange={handlePerPageChange}>
                        {perPageOptions.map(opt => <option key={opt} value={opt}>-- {opt}건 씩 조회 --</option>)}
                    </select>
                    <button type='button' onClick={search} disabled={isLoading ? 'disabled' : ''} className={`search_btn ${primaryBackColor}`}><Icons.Search16w />검색</button>
                    <ExcelDownload
                        excelHead={dataHead}
                        excelData={dataList}
                        searched={isSearched}
                        dataCode={dataColumn}
                        excelFileName={excelFileName}
                    />
                </div>
            </div>

            <div className='total_items mb-2'>전체&nbsp;<strong className={fontColor}>{pageInfo.totalItems}</strong>건</div>

            <div className='data_grid_container'>
                {isLoading ? <Loading /> :
                    <div className='flex flex-col w-full h-full overflow-hidden'>
                        <div className='data_responsive'>
                            <hr className={`border-t-2 mr-1-1 ${borderColorGray}`} />
                            <div className='data_list_table td_wrap'>
                                <table className={borderColorGray}>
                                    <thead>
                                        <tr>
                                            <th>
                                                <input
                                                    type='checkbox'
                                                    checked={dataList.length > 0 && checkItems.length === dataList.length}
                                                    onChange={(e) => handleAllCheck(e.target.checked)}
                                                />
                                            </th>
                                            {dataHead.map((head, idx) => <th key={idx}>{head}</th>)}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {dataList.length === 0 ? (
                                            <tr>
                                                <td colSpan={1000} className='empty_box'>
                                                    <span className={`data_empty ${lightBackColor}`}>
                                                        {isSearched ? <Icons.EmptyDalgu /> : <Icons.NullDalgu />}
                                                        {isSearched ? '해당하는 데이터가 없습니다.' : '검색 버튼을 눌러 데이터를 조회하세요.'}
                                                    </span>
                                                </td>
                                            </tr>
                                        ) : (
                                            dataList.map((item, index) => {
                                                const gridColor = index % 2 === 0 ? lightBackColor : '';
                                                return (
                                                    <tr key={item.SEQ ?? index} className={`${gridColor} ${hoverLightFontColor}`}>
                                                        <td>
                                                            <input
                                                                type='checkbox'
                                                                checked={checkItems.some(ci => ci.SEQ === item.SEQ)}
                                                                onChange={(e) => handleSingleCheck(e.target.checked, item)}
                                                            />
                                                        </td>
                                                        {dataColumn.map((col, idx) => <td key={idx}>{item[col]}</td>)}
                                                    </tr>
                                                )
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            <hr className='mr-1-1 co_border_gray' />
                        </div>
                        {(dataList.length === 0 || pageInfo.itemsPerPage > pageInfo.totalItems) ? <br /> : <Pagination paginationInfo={{ ...pageInfo, onPageChange: onHandlePage }} /> }
                    </div>
                }
            </div>
            <div>
                <p>- 선택처리 : 목록에서 체크한 건만 승인/반려 처리합니다.</p>
                <p>- 일괄처리 : 체크와 무관하게 검색조건(기관, 신청기간)에 해당하는 <strong>미승인 건 전체</strong>를 처리합니다. 기관을 선택하지 않으면 <strong>전체 기관</strong>이 대상입니다.</p>
                <p>- 처리정보의 <strong>전송마감 종료일자는 대상 선정 조건</strong>입니다. 목록의 [전송마감기간] 종료일자와 같은 건만 처리되고, 시작일자만 입력값으로 갱신됩니다. (일괄처리는 시작·종료일자 모두 갱신)</p>
                <p>- 선택처리는 <strong>승인상태와 무관하게 적용</strong>됩니다. 이미 승인·반려·신청취소된 건을 체크하면 그대로 덮어쓰므로 확인 후 처리하세요.</p>
                <p>- 신청취소는 자료를 삭제하지 않고 승인상태를 '신청취소'로 변경합니다. 이때 <strong>같은 환자(기관·등록번호·내원일시)의 신청 이력이 함께 취소</strong>되므로, 처리 결과 건수를 확인하세요.</p>
            </div>
        </Main>
    );
};

export default BdExceptionApp;
